import { User } from '../types';

// Permission utilities for role-based access control

export const PERMISSIONS = {
  // System access
  SYSTEM_ACCESS: 'SYSTEM_ACCESS',
  MANAGE_SYSTEM: 'MANAGE_SYSTEM',
  DATABASE_ACCESS: 'DATABASE_ACCESS',
  SECURITY_OVERRIDE: 'SECURITY_OVERRIDE',
  CREATE_ADMINS: 'CREATE_ADMINS',
  
  // User management
  MANAGE_USERS: 'MANAGE_USERS',
  VIEW_MEMBERS: 'VIEW_MEMBERS',
  ADD_MEMBERS: 'ADD_MEMBERS',
  EDIT_MEMBERS: 'EDIT_MEMBERS',
  DELETE_MEMBERS: 'DELETE_MEMBERS',
  
  // Financial operations
  VIEW_FINANCES: 'VIEW_FINANCES',
  MANAGE_CONTRIBUTIONS: 'MANAGE_CONTRIBUTIONS',
  MANAGE_EXPENSES: 'MANAGE_EXPENSES',
  APPROVE_FINANCES: 'APPROVE_FINANCES',
  APPROVE_BENEFITS: 'APPROVE_BENEFITS',
  VIEW_BENEFITS: 'VIEW_BENEFITS',
  
  // Contribution editing permissions
  EDIT_OWN_CONTRIBUTIONS: 'EDIT_OWN_CONTRIBUTIONS', // Self-editing within 24 hours
  EDIT_ANY_CONTRIBUTION: 'EDIT_ANY_CONTRIBUTION', // Finance Admins editing any contribution in their town
  EDIT_CONTRIBUTION_ADMIN: 'EDIT_CONTRIBUTION_ADMIN', // Admins/Super Admins editing any contribution
  
  // Communication
  SEND_SMS: 'SEND_SMS',
  
  // Reporting and audit
  VIEW_REPORTS: 'VIEW_REPORTS',
  VIEW_AUDIT_LOGS: 'VIEW_AUDIT_LOGS',
  
  // Event management
  MANAGE_EVENTS: 'MANAGE_EVENTS',
  VIEW_EVENTS: 'VIEW_EVENTS',
  
  // Benefit management
  MANAGE_BENEFITS: 'MANAGE_BENEFITS',
  REQUEST_BENEFITS: 'REQUEST_BENEFITS',
  
  // Town management
  VIEW_TOWNS: 'VIEW_TOWNS',
  MANAGE_TOWNS: 'MANAGE_TOWNS'
};

export const ROLE_PERMISSIONS = {
  developer: [
    PERMISSIONS.SYSTEM_ACCESS,
    PERMISSIONS.MANAGE_USERS,
    PERMISSIONS.VIEW_MEMBERS,
    PERMISSIONS.ADD_MEMBERS,
    PERMISSIONS.EDIT_MEMBERS,
    PERMISSIONS.DELETE_MEMBERS,
    PERMISSIONS.VIEW_FINANCES,
    PERMISSIONS.MANAGE_CONTRIBUTIONS,
    PERMISSIONS.MANAGE_EXPENSES,
    PERMISSIONS.APPROVE_FINANCES,
    PERMISSIONS.APPROVE_BENEFITS,
    PERMISSIONS.VIEW_BENEFITS,
    PERMISSIONS.VIEW_REPORTS,
    PERMISSIONS.VIEW_AUDIT_LOGS,
    PERMISSIONS.SEND_SMS,
    PERMISSIONS.MANAGE_SYSTEM,
    PERMISSIONS.DATABASE_ACCESS,
    PERMISSIONS.SECURITY_OVERRIDE,
    PERMISSIONS.CREATE_ADMINS,
    PERMISSIONS.EDIT_OWN_CONTRIBUTIONS,
    PERMISSIONS.EDIT_ANY_CONTRIBUTION,
    PERMISSIONS.EDIT_CONTRIBUTION_ADMIN,
    PERMISSIONS.MANAGE_EVENTS,
    PERMISSIONS.VIEW_EVENTS,
    PERMISSIONS.MANAGE_BENEFITS,
    PERMISSIONS.REQUEST_BENEFITS,
    PERMISSIONS.VIEW_TOWNS,
    PERMISSIONS.MANAGE_TOWNS
  ],
  
  admin: [
    PERMISSIONS.SYSTEM_ACCESS,
    PERMISSIONS.MANAGE_USERS,
    PERMISSIONS.VIEW_MEMBERS,
    PERMISSIONS.ADD_MEMBERS,
    PERMISSIONS.EDIT_MEMBERS,
    PERMISSIONS.DELETE_MEMBERS,
    PERMISSIONS.VIEW_FINANCES,
    PERMISSIONS.MANAGE_CONTRIBUTIONS,
    PERMISSIONS.MANAGE_EXPENSES,
    PERMISSIONS.APPROVE_FINANCES,
    PERMISSIONS.APPROVE_BENEFITS,
    PERMISSIONS.VIEW_BENEFITS,
    PERMISSIONS.VIEW_REPORTS,
    PERMISSIONS.VIEW_AUDIT_LOGS,
    PERMISSIONS.SEND_SMS,
    PERMISSIONS.MANAGE_SYSTEM,
    PERMISSIONS.DATABASE_ACCESS,
    PERMISSIONS.SECURITY_OVERRIDE,
    PERMISSIONS.CREATE_ADMINS,
    PERMISSIONS.EDIT_OWN_CONTRIBUTIONS,
    PERMISSIONS.EDIT_ANY_CONTRIBUTION,
    PERMISSIONS.EDIT_CONTRIBUTION_ADMIN,
    PERMISSIONS.MANAGE_EVENTS,
    PERMISSIONS.VIEW_EVENTS,
    PERMISSIONS.MANAGE_BENEFITS,
    PERMISSIONS.REQUEST_BENEFITS,
    PERMISSIONS.VIEW_TOWNS,
    PERMISSIONS.MANAGE_TOWNS
  ],
  
  finance_admin: [
    PERMISSIONS.VIEW_MEMBERS,
    PERMISSIONS.VIEW_FINANCES,
    PERMISSIONS.MANAGE_CONTRIBUTIONS,
    PERMISSIONS.MANAGE_EXPENSES,
    PERMISSIONS.APPROVE_FINANCES,
    PERMISSIONS.APPROVE_BENEFITS,
    PERMISSIONS.VIEW_BENEFITS,
    PERMISSIONS.VIEW_REPORTS,
    PERMISSIONS.VIEW_AUDIT_LOGS,
    PERMISSIONS.EDIT_OWN_CONTRIBUTIONS,
    PERMISSIONS.EDIT_ANY_CONTRIBUTION,
    PERMISSIONS.MANAGE_BENEFITS,
    PERMISSIONS.REQUEST_BENEFITS,
    PERMISSIONS.VIEW_TOWNS
  ],
  
  town_admin: [
    PERMISSIONS.VIEW_MEMBERS,
    PERMISSIONS.ADD_MEMBERS,
    PERMISSIONS.EDIT_MEMBERS,
    PERMISSIONS.VIEW_FINANCES,
    PERMISSIONS.MANAGE_CONTRIBUTIONS,
    PERMISSIONS.MANAGE_EXPENSES,
    PERMISSIONS.VIEW_REPORTS,
    PERMISSIONS.SEND_SMS,
    PERMISSIONS.EDIT_OWN_CONTRIBUTIONS,
    PERMISSIONS.MANAGE_EVENTS,
    PERMISSIONS.VIEW_EVENTS,
    PERMISSIONS.REQUEST_BENEFITS,
    PERMISSIONS.VIEW_TOWNS
  ],
  
  member_admin: [
    PERMISSIONS.VIEW_MEMBERS,
    PERMISSIONS.ADD_MEMBERS,
    PERMISSIONS.EDIT_MEMBERS,
    PERMISSIONS.VIEW_REPORTS,
    PERMISSIONS.EDIT_OWN_CONTRIBUTIONS,
    PERMISSIONS.VIEW_EVENTS,
    PERMISSIONS.REQUEST_BENEFITS,
    PERMISSIONS.VIEW_TOWNS
  ],
  
  member: [
    PERMISSIONS.VIEW_MEMBERS,
    PERMISSIONS.VIEW_FINANCES,
    PERMISSIONS.VIEW_BENEFITS,
    PERMISSIONS.EDIT_MEMBERS, // Can edit own profile
    PERMISSIONS.EDIT_OWN_CONTRIBUTIONS,
    PERMISSIONS.VIEW_EVENTS,
    PERMISSIONS.REQUEST_BENEFITS,
    PERMISSIONS.VIEW_TOWNS
  ]
};

export const hasPermission = (userPermissions: string[], requiredPermission: string): boolean => {
  return userPermissions.includes(requiredPermission);
};

export const hasAnyPermission = (userPermissions: string[], requiredPermissions: string[]): boolean => {
  return requiredPermissions.some(permission => userPermissions.includes(permission));
};

export const canAccessTownData = (userPermissions: string[], userTownId: string | null, targetTownId: string): boolean => {
  // Super admins and finance admins can access all town data
  if (userPermissions.includes(PERMISSIONS.MANAGE_USERS) || userPermissions.includes(PERMISSIONS.APPROVE_FINANCES)) {
    return true;
  }
  
  // Town admins can only access their own town data
  return userTownId === targetTownId;
};

export const isSuperAdmin = (user: User): boolean => {
  return user && ['super_admin', 'admin', 'developer'].includes(user.role);
};

export const isFinanceAdmin = (user: User): boolean => {
  return user && ['admin', 'finance_admin', 'developer'].includes(user.role);
};

export const isAdmin = (user: User): boolean => {
  return user && ['admin', 'finance_admin', 'town_admin', 'member_admin', 'developer'].includes(user.role);
};

export const isMember = (user: User): boolean => {
  return user && user.role === 'member';
};

export const isDeveloper = (user: User): boolean => {
  return user && user.role === 'developer';
};